<script setup lang="ts">
import { useCourseExemptionMutations } from '@/composables/mutations/tertiary/useCourseExemptionMutations';
import { useCourses } from '@/composables/queries/useCourses';
import { formatCourseInstance } from '@/utils/courseInstance';
import StudentSearchInput from '@/components/common/StudentSearchInput.vue';
import { EnrollmentStatusEnum, type Student } from '@/types/composition-functions';

const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { addStudentCourseToExempt, addingExemption } = useCourseExemptionMutations();

// Course search
const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit?: number; course_instance_id?: string; course_instance_year?: number | null }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit ?? '?'} units)`,
    value: c.id,
    code: c.course_code,
    title: c.course_title,
    unit: c.unit,
    instance: formatCourseInstance(c),
  })),
);

let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => {
    setCourseFilter({ query: event.value });
  }, 300);
}

// Form state
const selectedStudent = ref<Student | null>(null);
const selectedCourse = ref<string | null>(null);

async function handleSubmit() {
  if (!selectedStudent.value?.id || !selectedCourse.value) return;
  const result = await addStudentCourseToExempt({
    student_id: String(selectedStudent.value.id),
    course_id: selectedCourse.value,
  });
  if (result?.success) {
    selectedStudent.value = null;
    selectedCourse.value = null;
    visible.value = false;
    emit('success');
  }
}

function onHide() {
  selectedStudent.value = null;
  selectedCourse.value = null;
}
</script>

<template>
  <Dialog data-testid="add-exemption-dialog" v-model:visible="visible" header="Add Course Exemption" modal :style="{ width: '30rem' }" @hide="onHide">
    <div class="flex flex-col gap-4 pt-2">
      <div class="flex flex-col gap-1" data-testid="exemption-student-select">
        <StudentSearchInput
          v-model="selectedStudent"
          label="Student"
          placeholder="Search by name or matric number..."
          :filter="{ status: EnrollmentStatusEnum.Enrolled }"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Course</label>
        <Select
          data-testid="exemption-course-select"
          v-model="selectedCourse"
          :options="courseOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select course..."
          filter
          @filter="onCourseFilter"
          class="w-full"
        >
          <template #option="{ option }">
            <div class="flex flex-col py-0.5">
              <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
              <span class="text-xs text-gray-400">Unit: {{ option.unit ?? '—' }} · {{ option.instance || '—' }}</span>
            </div>
          </template>
        </Select>
      </div>
    </div>

    <template #footer>
      <Button size="small" data-testid="exemption-add-cancel" label="Cancel" severity="secondary" text @click="visible = false" />
      <Button size="small"
        data-testid="exemption-add-submit"
        label="Add Exemption"
        icon="pi pi-plus"
        @click="handleSubmit"
        :loading="addingExemption"
        :disabled="!selectedStudent?.id || !selectedCourse"
      />
    </template>
  </Dialog>
</template>
